import * as React from 'react';
import { Check, ChevronDown } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { FormError } from '@/components/ui/form-field';
import { Label } from '@/components/ui/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';

export type ComboboxOption = {
  value: string | number;
  label: string;
};

// Permissive setter signature compatible with both Formik's setFieldValue
// (Promise + value: any) AND narrow ad-hoc handlers like
// `(field, value: string) => void`. The `any` here is load-bearing: a
// narrower type breaks call sites that pass string-typed handlers because
// function-parameter contravariance prevents them from accepting `unknown`.
// Banked: a project-wide migration to a single typed handler shape would
// let this become `(field: string, value: unknown, additionalData?: unknown) => unknown`.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type SetValueFn = (field: string, value: any, additionalData?: any) => unknown;

export interface ComboboxProps {
  options?: ComboboxOption[];
  value?: ComboboxOption | string | number | null | '';
  name?: string;
  controlId?: string;
  ariaDescribedBy?: string;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  label?: React.ReactNode;
  isRequired?: boolean;
  errors?: string | boolean | null;
  disabled?: boolean;
  setFieldValue?: SetValueFn;
  field?: string;
  /** Forwarded to setFieldValue's 3rd arg; opaque to this component. */
  additionalData?: unknown;
  className?: string;
  md?: string;
}

const widthClassFromMd = (md?: string): string => {
  if (!md) return '';
  const map: Record<string, string> = {
    '1': 'w-1/12',
    '2': 'w-2/12',
    '3': 'w-3/12',
    '4': 'w-4/12',
    '5': 'w-5/12',
    '6': 'w-6/12',
    '7': 'w-7/12',
    '8': 'w-8/12',
    '9': 'w-9/12',
    '10': 'w-10/12',
    '11': 'w-11/12',
    '12': 'w-full',
  };
  return map[md] ?? '';
};

const normalizeValue = (
  value: ComboboxProps['value']
): string | undefined => {
  if (value == null || value === '') return undefined;
  if (typeof value === 'object') return String(value.value);
  return String(value);
};

/**
 * Searchable select: a Popover + Command palette over an option list, with keyboard navigation.
 *
 * @category Forms
 */
export const Combobox = React.forwardRef<HTMLButtonElement, ComboboxProps>(
  function Combobox(
    {
      options,
      value,
      name,
      controlId,
      ariaDescribedBy,
      placeholder,
      searchPlaceholder = 'Search...',
      emptyMessage = 'No results found.',
      label,
      isRequired,
      errors,
      disabled,
      setFieldValue,
      field,
      additionalData,
      className,
      md,
    },
    ref
  ) {
    const [open, setOpen] = React.useState(false);
    const widthClass = widthClassFromMd(md);
    const triggerId = controlId ? `htmlFor_${controlId}` : undefined;
    const errorMessage = typeof errors === 'string' ? errors : null;
    const hasError = Boolean(errors);

    const safeOptions = options ?? [];
    const externalValue = normalizeValue(value);

    // Internal display state — many consumers only update the `value` prop in
    // edit-mode hydration, not on every user selection. Mirroring the legacy
    // SearchSelectUI contract so the trigger reflects user choices optimistically.
    const [internalValue, setInternalValue] = React.useState<string | undefined>(
      externalValue
    );
    React.useEffect(() => {
      setInternalValue(externalValue);
    }, [externalValue]);

    const selected = safeOptions.find(
      (o) => String(o.value) === internalValue
    );

    const handleSelect = (newValue: string) => {
      setInternalValue(newValue);
      const matched = safeOptions.find((o) => String(o.value) === newValue);
      if (setFieldValue && field) {
        setFieldValue(field, matched ? matched.value : newValue, additionalData);
      }
      setOpen(false);
    };

    return (
      <div className={cn(widthClass, className)}>
        {label && (
          <Label htmlFor={triggerId} required={isRequired}>
            {label}
          </Label>
        )}
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              ref={ref}
              id={triggerId}
              type="button"
              variant="outline"
              role="combobox"
              aria-expanded={open}
              aria-describedby={ariaDescribedBy}
              aria-invalid={hasError || undefined}
              disabled={disabled}
              name={name}
              className={cn(
                'w-full justify-between bg-background font-normal',
                !selected && 'text-muted-foreground',
                hasError && 'border-destructive'
              )}
            >
              <span className="truncate">
                {selected ? selected.label : placeholder}
              </span>
              <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent
            align="start"
            className="w-[var(--radix-popover-trigger-width)] p-0"
          >
            <Command>
              <CommandInput placeholder={searchPlaceholder} />
              <CommandList>
                <CommandEmpty>{emptyMessage}</CommandEmpty>
                <CommandGroup>
                  {safeOptions.map((o) => {
                    const stringVal = String(o.value);
                    const isSelected = stringVal === internalValue;
                    return (
                      <CommandItem
                        key={stringVal}
                        value={`${o.label} ${stringVal}`}
                        onSelect={() => handleSelect(stringVal)}
                        className={cn(
                          isSelected && 'font-medium text-primary'
                        )}
                      >
                        <span className="flex-1 truncate">{o.label}</span>
                        {isSelected && (
                          <Check className="ml-auto h-4 w-4 text-primary" />
                        )}
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
        <FormError message={errorMessage} />
      </div>
    );
  }
);
